import React from 'react';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger, PopoverPortal } from '@radix-ui/react-popover';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { reservationMessages } from 'src/features/reservations/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Textarea } from 'src/registry/ui/Textarea';
import { generalMessages } from 'src/utils/messages';

type ReservationNameTypeDateProps = {
    selectedFromDate: Date | undefined;
    setSelectedFromDate: (date: Date | undefined) => void;
};

const ReservationNameTypeDate = ({ selectedFromDate, setSelectedFromDate }: ReservationNameTypeDateProps) => {
    const { control, setValue } = useFormContext();
    const { formatMessage, formatDate } = useIntl();
    const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);

    const selectedDateToDisplay = () => {
        const today = new Date();

        if (selectedFromDate) {
            return selectedFromDate && selectedFromDate < today ? formatDate(today) : formatDate(selectedFromDate);
        } else {
            return selectedFromDate ? formatDate(selectedFromDate) : formatMessage(generalMessages.pickADate);
        }
    };

    return (
        <>
            <FormField
                control={control}
                name="name"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(reservationMessages.name)}
                        </FormLabel>
                        <div className="flex w-full gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder={formatMessage(reservationMessages.versionName)}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </div>
                    </FormItem>
                )}
            />
            <FormField
                control={control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(reservationMessages.description)}</FormLabel>
                        <div className="flex w-full gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Textarea
                                        placeholder={formatMessage(reservationMessages.versionDescription)}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </div>
                    </FormItem>
                )}
            />
            <FormField
                control={control}
                name="valid_from"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(reservationMessages.reservationValidFrom)}
                            </FormLabel>
                            <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
                                <PopoverTrigger asChild>
                                    <Button
                                        id="date"
                                        variant="outline"
                                        className="text-base font-normal"
                                        onClick={() => setOpenCalendar(true)}
                                    >
                                        <FontAwesomeIcon icon={faCalendar} className="text-primary-700 mr-1 size-4" />
                                        {selectedDateToDisplay()}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverPortal>
                                    <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                        <Calendar
                                            mode="single"
                                            selected={selectedFromDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedFromDate(date);
                                                    setValue('valid_from', date, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                    setOpenCalendar(!openCalendar);
                                                }
                                            }}
                                            disabled={{ before: new Date() }}
                                        />
                                    </PopoverContent>
                                </PopoverPortal>
                            </Popover>
                        </FormItem>
                    );
                }}
            />
        </>
    );
};

export default ReservationNameTypeDate;
